import React, { useCallback } from 'react';
import type { VipFragment } from '@graphql/fragments/vip.graphql.generated';
import { getInitialsFromName } from '@shared/utils/getInitials';
import { ColumnsV2, Box, SpacingStack } from '@withjoy/joykit';
import type { ColumnV2Props, BoxProps } from '@withjoy/joykit';
import { ProfileDialog, useProfileDialog } from '@shared/components/ProfileDialog';
import type { WeddingPartyListTranslations } from './WeddingPartyList.i18n';
import { useWeddingPartyListTranslations } from './WeddingPartyList.i18n';
import { StyledItemInitials, StyledItemAvatar, StyledWedginPartyText } from './WeddingPartyList.styles';
import { withLazyPropOnIntersection } from '@shared/utils/hooks/useLazyLoadImage';
import { addRendition } from '@shared/utils/photoRendition';
import { TelemetryProvider, useWeddingPartyListTelemetry } from './WeddingPartyList.telemetry';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { hed5ToSubHeadingOverride, label6ToCaptionOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useLayout } from '@shared/context/LayoutProvider';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { pxToRem } from '@withjoy/joykit/theme';
import { useGetVips } from './useGetVips';
import { applyVipPresentationSettings } from '../../common/utils/applyVipPresentationSettings';

export interface WeddingPartyListProps extends BoxProps {
  children?: never;
  graphicAccent: Maybe<string> | undefined;
  columnCount?: ColumnV2Props['span'];
  eventId: string;
  eventHandle?: string;
  isCustomBlock?: boolean;
  /**
   * Optional presentational settings for this list.
   */
  presentationSettings?: {
    /**
     * IDs that will be excluded from the list.
     */
    excludedIDs?: string[];
    /**
     * Array of VIP IDs in the order they should be displayed.
     * Omitted IDs will be displayed after the included IDs in the order they were returned from the API.
     */
    sortOrder?: string[];
  };
}

export interface WeddingPartyContentProps extends BoxProps {
  children?: never;
  graphicAccent: Maybe<string> | undefined;
  columnCount?: ColumnV2Props['span'];
  eventId: string;
  list: VipFragment[];
}

const FULL_BLEED_MARGIN_X_MOBILE = -18;

const LazyStyledItemAvatar = withLazyPropOnIntersection(StyledItemAvatar, 'backgroundImage', 200);
const WeddingPartyListItem: React.FC<{
  columnCount?: ColumnV2Props['span'];
  item: VipFragment;
  index: number;
  translations: WeddingPartyListTranslations;
  onOpenProfileDialog: (index: number) => void;
  isCircle: boolean;
}> = React.memo(({ columnCount, index, item, translations, onOpenProfileDialog, isCircle }) => {
  const { photo, alias } = item;
  const { ariaLabel, title } = translations;
  const handleClick = () => onOpenProfileDialog(index);

  // Prefer "photoUrl" if it exists. But fall back to photo for backwards compat.
  // (Photo is stored on the VIP firebase object, which is deprecated)
  const photoUrl = item.photoUrl ?? photo?.url;

  return (
    <>
      <ColumnsV2.Column
        span={columnCount}
        style={{
          paddingLeft: pxToRem(2),
          paddingRight: pxToRem(2),
        }}
      >
        <Box
          overflow="hidden"
          marginX={{ _: FULL_BLEED_MARGIN_X_MOBILE, xxs: 'unset' }}
        >
          <LazyStyledItemAvatar
            role="img"
            aria-label={ariaLabel}
            data-testid="vip-photo"
            $isCircle={isCircle}
            $hasPhoto={!!photoUrl}
            backgroundImage={{
              onscreen: photoUrl
                ? `url("${addRendition({
                    url: photoUrl,
                    renditionSize: 'small',
                  })}")`
                : undefined,
            }}
            onClick={handleClick}
          >
            {!photoUrl && alias && <StyledItemInitials data-testid="vip-initials">{getInitialsFromName(alias)}</StyledItemInitials>}
          </LazyStyledItemAvatar>
        </Box>
        <SpacingStack
          data-testid="vip-index-item"
          cursor={'pointer'}
          textAlign="center"
          marginTop={5}
          onClick={handleClick}
          spacing={{ _: 1, sm2: 2 }}
        >
          {alias && (
            <GuestSiteTypographyOverride override={hed5ToSubHeadingOverride}>
              <StyledWedginPartyText
                typographyVariant="hed5"
                fontWeight={700}
                data-testid="vip-alias"
              >
                {alias}
              </StyledWedginPartyText>
            </GuestSiteTypographyOverride>
          )}
          {title && (
            <GuestSiteTypographyOverride override={label6ToCaptionOverride}>
              <StyledWedginPartyText
                typographyVariant="label6"
                fontWeight={400}
                data-testid={'vip-title'}
              >
                {title}
              </StyledWedginPartyText>
            </GuestSiteTypographyOverride>
          )}
        </SpacingStack>
      </ColumnsV2.Column>
    </>
  );
});

const WeddingPartyContent = React.forwardRef<HTMLDivElement, WeddingPartyContentProps>(({ graphicAccent, list, columnCount = [6, 4], eventId: eventIdProp, ...restProps }, ref) => {
  const { getItemTranslations } = useWeddingPartyListTranslations();
  const { activeIndex, isOpen, openProfileDialog, closeProfileDialog } = useProfileDialog();
  const telemetry = useWeddingPartyListTelemetry();

  const [isWeddingPartyListCircleAvatarsEnabled] = useIsFeatureEnabled('weddingPartyListCircleAvatars');

  const handleOpenedDialog = useCallback(
    index => {
      openProfileDialog(index);
      telemetry.VIPDetailsOpened(index);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleCloseDialog = useCallback(() => {
    closeProfileDialog();
    telemetry.VIPDetailsClosed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      ref={ref}
      {...restProps}
    >
      <ColumnsV2
        rowGap={8}
        useContainer={false}
      >
        {list.map((item, index) => {
          const translations = getItemTranslations(item);
          return (
            <WeddingPartyListItem
              key={item.id}
              item={item}
              index={index}
              columnCount={columnCount}
              translations={translations}
              onOpenProfileDialog={() => handleOpenedDialog(index)}
              isCircle={isWeddingPartyListCircleAvatarsEnabled}
            />
          );
        })}
      </ColumnsV2>
      <ProfileDialog
        activeIndex={activeIndex}
        graphicAccent={graphicAccent}
        list={list}
        isOpen={isOpen}
        onClose={handleCloseDialog}
      />
    </Box>
  );
});

const WeddingPartyList = React.forwardRef<HTMLDivElement, WeddingPartyListProps>(({ graphicAccent, columnCount = [6, 4], ...restProps }, ref) => {
  const { layout } = useLayout();
  const { eventId, eventHandle, presentationSettings, isCustomBlock } = restProps;

  const { vips, loading } = useGetVips({ eventId, eventHandle, isCustomBlock });

  if (loading) return <div style={{ height: '300px' }}></div>; // height set to prevent divs from jumping around while page loads

  const list = applyVipPresentationSettings(vips, presentationSettings);

  return (
    <TelemetryProvider context={{ eventId: eventId, layout: layout }}>
      <WeddingPartyContent
        ref={ref}
        {...restProps}
        columnCount={columnCount}
        list={list}
        graphicAccent={graphicAccent}
      />
    </TelemetryProvider>
  );
});

WeddingPartyList.displayName = 'WeddingPartyList';

export { WeddingPartyList };
